import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form, Field, useFormikContext } from "formik";
import * as yup from "yup";
import TopImage from "../../../common/TopImage";
import { Images } from "../../../../../config/images";
import "./License.css";
import "../../Dashboard/Dashboard.css";
import BackButton from "../../../../common/BackButton";
import { handleSmartBack } from "../../../../../utils/navigationHelper";
import CustomInput from "../../../../common/CustomInput";
import { useGetLicensePrice } from "../../../../../hooks/licenses/useGetLicensePrice";
import { useGetOrganizationStats } from "../../../../../hooks/organization/useGetOrganizationStats";
import { useGetLicenseOverview } from "../../../../../hooks/school/licenses/useGetLicenseOverview";
import { useAddOnLicenses } from "../../../../../hooks/licenses/useAddOnLicenses";
import DashboardStats from "../../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../../common/Dashboard/DashboardStatsSkeleton";
import SuccessModal from "../../../../common/SuccessModal";
import { MAX_LICENSE_ADD_ON } from "../../../../../constant/constant";
import { Button } from "react-bootstrap";

/**
 * @typedef {"school" | "organization"} tenanttype
 */

/**
 * @typedef {"buy" | "add-on"} LicenseType
 */

/**
 * Price Summary Component - Shows price calculation for add-on licenses
 */
const PriceSummary = ({
  priceData,
  priceLoading,
  priceError,
  onRetry,
  currency,
}) => {
  const { values } = useFormikContext();

  const totalPrice = useMemo(() => {
    if (priceData?.price_per_license && values.numberOfLicenses > 0) {
      return (priceData.price_per_license * values.numberOfLicenses).toFixed(2);
    }
    return null;
  }, [priceData, values.numberOfLicenses]);

  return (
    <div className="form-row pt-3">
      <div className="price-summary-card">
        <h4 className="price-summary-title">Price Summary</h4>
        {priceLoading ? (
          <div className="price-loading">Loading price...</div>
        ) : priceError ? (
          <div className="price-error-container">
            <div className="price-error">{priceError}</div>
            <button type="button" className="retry-btn" onClick={onRetry}>
              <i className="bi bi-arrow-clockwise"></i> Retry
            </button>
          </div>
        ) : priceData?.price_per_license ? (
          <div className="price-details">
            <div className="price-row">
              <span className="price-label">Price per License:</span>
              <span className="price-value">
                {priceData.price_per_license} ({currency})
              </span>
            </div>
            <div className="price-row">
              <span className="price-label">Number of Licenses:</span>
              <span className="price-value">
                {values.numberOfLicenses || 0}
              </span>
            </div>
            <div className="price-row price-total">
              <span className="price-label">Total Amount:</span>
              <span className="price-value total-price">
                {totalPrice || "0.00"} ({currency})
              </span>
            </div>
          </div>
        ) : (
          <div className="price-error-container">
            <div className="price-error">Unable to fetch price information</div>
            <button type="button" className="retry-btn" onClick={onRetry}>
              <i className="bi bi-arrow-clockwise"></i> Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * purchaselicenses component - purchase form with payment method selection
 *
 * @param {{ type?: tenanttype }} props
 */
export const PurchaseLicenses = ({ type = "school" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /** @type {LicenseType} */
  const licenseType =
    searchParams.get("license-type") === "add-on" ? "add-on" : "buy";

  // Hook for fetching license price (for add-on type)
  const {
    priceData,
    loading: priceLoading,
    error: priceError,
    getLicensePrice,
  } = useGetLicensePrice();

  // Hook for fetching organization stats (for organization type)
  const {
    stats: orgStats,
    loading: orgStatsLoading,
    error: orgStatsError,
    getOrganizationStats,
  } = useGetOrganizationStats();

  // Hook for fetching school license overview (for school type)
  const {
    licenseData: schoolLicenseData,
    loading: schoolStatsLoading,
    error: schoolStatsError,
    getLicenseOverview,
  } = useGetLicenseOverview();

  // Hook for requesting add-on licenses (online payment)
  const {
    requestAddOnLicenses,
    loading: addOnLoading,
    error: addOnError,
    pendingInvoice,
  } = useAddOnLicenses();

  // Modal state for error handling
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    type: "error",
    title: "Error",
    message: "",
    secondaryButtonText: "",
    secondaryButtonLink: "",
  });

  // Fetch price when license type is add-on
  useEffect(() => {
    if (licenseType === "add-on") {
      getLicensePrice();
    }
  }, [licenseType]);

  // Fetch stats based on type on mount
  useEffect(() => {
    if (type === "school") {
      getLicenseOverview();
    } else {
      getOrganizationStats();
    }
  }, [type]);

  // Determine loading and error states based on type
  const statsLoading = type === "school" ? schoolStatsLoading : orgStatsLoading;
  const statsError = type === "school" ? schoolStatsError : orgStatsError;
  const statsRetry =
    type === "school" ? getLicenseOverview : getOrganizationStats;

  // Prepare stats for DashboardStats component based on type
  const licenseStatsData = useMemo(() => {
    if (type === "school") {
      const schoolStats = schoolLicenseData?.stats;
      if (!schoolStats) return [];
      return [
        {
          label: "Total Licenses",
          value: (schoolStats.total_licenses ?? 0).toLocaleString(),
          icon: "fa-id-card",
          colorClass: "dashboard-stat-card-green",
        },
        {
          label: "Used Licenses",
          value: (schoolStats.used_licenses ?? 0).toLocaleString(),
          icon: "fa-user-check",
          colorClass: "dashboard-stat-card-purple",
        },
        {
          label: "Available Licenses",
          value: (schoolStats.available_licenses ?? 0).toLocaleString(),
          icon: "fa-users",
          colorClass: "dashboard-stat-card-peach",
        },
      ];
    } else {
      const orgLicenses = orgStats?.licenses;
      if (!orgLicenses) return [];
      return [
        {
          label: "Total Licenses",
          value: (orgLicenses.total ?? 0).toLocaleString(),
          icon: "fa-id-card",
          colorClass: "dashboard-stat-card-green",
        },
        {
          label: "Assigned Licenses",
          value: (orgLicenses.assigned ?? 0).toLocaleString(),
          icon: "fa-user-check",
          colorClass: "dashboard-stat-card-purple",
        },
        {
          label: "Remaining Licenses",
          value: (orgLicenses.remaining ?? 0).toLocaleString(),
          icon: "fa-users",
          colorClass: "dashboard-stat-card-peach",
        },
      ];
    }
  }, [type, schoolLicenseData, orgStats]);

  // Validation schema with Yup
  const validationSchema = yup.object().shape({
    numberOfLicenses: yup
      .number()
      .typeError("Please enter a valid number")
      .required("Number of licenses is required")
      .min(1, "Number of licenses must be at least 1")
      .max(
        MAX_LICENSE_ADD_ON,
        `Number of licenses cannot exceed ${MAX_LICENSE_ADD_ON.toLocaleString()}`,
      )
      .integer("Number of licenses must be a whole number"),
    paymentMethod: yup
      .string()
      .required("Payment method is required")
      .oneOf(["online", "manual"], "Invalid payment method"),
  });

  // Initial form values
  const initialValues = {
    numberOfLicenses: "",
    paymentMethod: "online",
  };

  const handleSubmit = async (values) => {
    if (values.paymentMethod === "manual") {
      // Prepare add-on license data to pass to ManualPaymentForm
      const addOnData = {
        addonLicenses: values.numberOfLicenses,
        pricePerLicense: priceData?.price_per_license || 0,
        totalAmount:
          (priceData?.price_per_license || 0) * values.numberOfLicenses,
        licenseType: licenseType,
        currency: priceData?.currency || "INR",
      };

      if (type === "school") {
        navigate(
          "/school-admin/settings/purchase-licenses/manual-payment-form",
          {
            state: addOnData,
          },
        );
      } else {
        navigate(
          "/organization/settings/purchase-licenses/manual-payment-form",
          {
            state: addOnData,
          },
        );
      }
    } else {
      // Handle online payment
      const result = await requestAddOnLicenses({
        addon_licenses: values.numberOfLicenses,
        price_per_license: priceData?.price_per_license || 0,
        amount: (priceData?.price_per_license || 0) * values.numberOfLicenses,
        source: "online",
        currency: priceData?.currency || "INR",
      });

      if (result.success) {
        const invoiceId = result.data?.invoice_id;
        const prefix = type === "school" ? "/school-admin" : "/organization";
        setModalConfig({
          type: "success",
          title: "Invoice Created",
          message: `Your addon license invoice (#${invoiceId}) has been created successfully. You can pay now or pay later.`,
          secondaryButtonText: "Pay Later",
          secondaryButtonLink: `${prefix}/settings/invoices`,
          primaryButtonText: "Pay Now",
          primaryButtonLink: `${prefix}/${invoiceId}/payment`,
        });
        setShowModal(true);
      } else if (result.data?.status === "unpaid_exists") {
        // 422: unpaid invoice already exists
        setModalConfig({
          type: "error",
          title: "Unpaid Invoice Exists",
          message: `An unpaid addon invoice already exists (Invoice #${result.data.invoice_id}). Please complete or cancel it before requesting a new addon.`,
          secondaryButtonText: "View Invoices",
          secondaryButtonLink:
            type === "school"
              ? "/school-admin/settings/invoices"
              : "/organization/settings/invoices",
        });
        setShowModal(true);
      } else {
        // Other errors
        setModalConfig({
          type: "error",
          title: "Error",
          message: addOnError || "Something went wrong. Please try again.",
        });
        setShowModal(true);
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className="">
        <div className="pb-2">
          <BackButton />
        </div>
        <section>
          <div className="container-fluid">
            <TopImage imageSrc={Images.TopImage} text="PURCHASE LICENSES" />
          </div>
        </section>

        <div className="purchase-form-container mt-4">
          {/* License Stats Header */}
          <div className="license-header">
            <h2 className="license-title">Licenses</h2>
          </div>

          {/* Restriction Check */}
          {licenseType === "add-on" &&
            !priceLoading &&
            priceData &&
            priceData.can_request_addon === false && (
              <div
                className="alert alert-warning text-center p-4 mb-4"
                role="alert"
              >
                <i className="bi bi-exclamation-triangle-fill fs-1 text-warning mb-3 d-block"></i>
                <h4 className="alert-heading fw-bold">Access Restricted</h4>
                <p className="mb-0 ">
                  Contact admin, your account is not activated yet for add-on
                  licenses.
                </p>
                <div className="mt-4">
                  <button
                    className="btn btn-primary px-4"
                    onClick={() => handleSmartBack(navigate, "/")}
                  >
                    Go Back
                  </button>
                </div>
              </div>
            )}

          {/* Stats Cards - Only show if not restricted or if restricted show nothing? User said "show make the ui to show this message... and go button". Usually usage stats might still be visible, but form should be hidden. Let's hide form. */
          /* Actually, if restricted, we should probably hide everything else or just the form? 
             The request says "show make the ui to show this message... and go back button".
             I will hide the form part if restricted.
          */}

          {/* Stats Cards */}
          {(!(licenseType === "add-on" &&
            !priceLoading &&
            priceData &&
            priceData.can_request_addon === false)) && (
              <>
                {statsLoading ? (
                  <DashboardStatsSkeleton count={3} />
                ) : statsError ? (
                  <div className="row g-3 mb-4">
                    <div className="col-12">
                      <div className="price-error-container">
                        <div className="price-error">{statsError}</div>
                        <button
                          type="button"
                          className="retry-btn"
                          onClick={statsRetry}
                        >
                          <i className="bi bi-arrow-clockwise"></i> Retry
                        </button>
                      </div>
                    </div>
                  </div>
                ) : licenseStatsData.length > 0 ? (
                  <DashboardStats stats={licenseStatsData} />
                ) : null}

                {/* Purchase Form */}
                <div className="purchase-form-section">
                  <h3 className="purchase-form-title">Purchase Licenses</h3>

                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ values, isSubmitting, errors, submitCount }) => {
                      return (
                        <Form>
                          <CustomInput
                            label="Number of New Licenses"
                            name="numberOfLicenses"
                            type="number"
                            placeholder="Enter number of licenses"
                            layout="stacked"
                          />

                          {/* Price Display Section for Add-on Licenses */}
                          {licenseType === "add-on" && (
                            <PriceSummary
                              priceData={priceData}
                              priceLoading={priceLoading}
                              priceError={priceError}
                              onRetry={getLicensePrice}
                              currency={priceData?.currency || "INR"}
                            />
                          )}

                          <div className="form-row pt-2">
                            <label className="form-label-custom">
                              Select Payment Method :
                            </label>
                            <div className="radio-group" role="group">
                              <div className="radio-option">
                                <Field
                                  type="radio"
                                  id="payOnline"
                                  name="paymentMethod"
                                  value="online"
                                />
                                <label htmlFor="payOnline">
                                  Pay Online{" "}
                                  <span className="radio-recommended">
                                    (Recommended)
                                  </span>
                                </label>
                              </div>
                              <div className="radio-option">
                                <Field
                                  type="radio"
                                  id="payManually"
                                  name="paymentMethod"
                                  value="manual"
                                />
                                <label htmlFor="payManually">Pay Manually</label>
                              </div>
                            </div>
                          </div>

                          <Button
                            type="submit"
                            className="submit-btn"
                            disabled={
                              addOnLoading ||
                              (licenseType === "add-on" &&
                                (priceLoading ||
                                  priceError ||
                                  !priceData?.price_per_license))
                            }
                          >
                            {addOnLoading ? "Processing..." : "Submit"}
                          </Button>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </>
            )}
        </div>
      </div>

      <SuccessModal
        showModal={showModal}
        handleClose={handleModalClose}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        primaryButtonText={modalConfig.primaryButtonText || "OK"}
        primaryButtonLink={modalConfig.primaryButtonLink || undefined}
        onPrimaryClick={
          !modalConfig.primaryButtonLink ? handleModalClose : undefined
        }
        secondaryButtonText={modalConfig.secondaryButtonText || undefined}
        secondaryButtonLink={modalConfig.secondaryButtonLink || undefined}
      />
    </>
  );
};

export default PurchaseLicenses;
